import * as React from 'react';
import { ButtonCoreIconProps } from 'omakase/Button/buttonIcons';
import {
  OmakasePolymorphicComponent,
  OmakasePolymorphicComponentProps,
  OmakasePolymorphicRootComponent,
} from '../utils/OmakasePolymorphicComponent';
import { Theme } from '@emotion/react';
import {
  buttonContent,
  buttonCoreBase,
  buttonCoreSize,
  buttonCoreVariant,
  buttonFullWidth,
  buttonIcon,
  buttonIconEnd,
  buttonIconOnly,
} from './buttonCssDefs';

export type ButtonCoreVariant =
  | 'basic'
  | 'ghost'
  | 'primary'
  | 'danger'
  | 'success'
  | 'highContrast'
  | 'basicBorderless'
  | 'ghostInverted';

export type ButtonCoreSize = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';

interface ButtonCoreWithSelectedCanSelectProps {
  /**
   * The selected state for the button
   */
  selected?: boolean;
  /**
   * The variant / color style for the button
   */
  variant: Extract<ButtonCoreVariant, 'basic'>;
}

interface ButtonCoreWithSelectedCannotSelectProps {
  /**
   * The selected state for the button
   */
  selected?: undefined;
  /**
   * The variant / color style for the button
   */
  variant: Exclude<ButtonCoreVariant, 'basic'>;
}

export type ButtonCoreWithSelectedProps =
  | ButtonCoreWithSelectedCanSelectProps
  | ButtonCoreWithSelectedCannotSelectProps;

export interface ButtonCoreWithoutSelectedProps {
  /**
   * The selected state for the button
   */
  selected?: undefined;
  /**
   * The variant / color style for the button
   */
  variant: ButtonCoreVariant;
}

export interface ButtonCoreBaseProps {
  /**
   * The aria-label for the button. Be sure to provide this if the button
   * doesn't have accessible text content for what it does, or might be
   * presented without that accessible text content.
   */
  'aria-label'?: string;
  /**
   * Whether or not the component is disabled
   */
  disabled?: boolean;
  /**
   * The href for an anchor component
   */
  href?: string;
  /**
   * * The click event. We may eventually want to make required when component is a button.
   */
  onClick?(e?: React.MouseEvent): void;
  /**
   * The size of the button
   */
  size: ButtonCoreSize;
}

export interface ButtonCoreIconOnlyProps {
  /**
   * An icon as the content
   */
  iconOnly?: boolean;
}

export interface ButtonCoreFullWidthProps {
  /**
   * Whether or not the button is full 100% width.
   */
  fullWidth?: boolean;
}

export type ButtonCoreProps = ButtonCoreBaseProps &
  ButtonCoreFullWidthProps &
  ButtonCoreIconOnlyProps &
  ButtonCoreIconProps &
  (ButtonCoreWithSelectedProps | ButtonCoreWithoutSelectedProps);

const ButtonCoreDefaultElement = 'button';

type ButtonPropsContextProps = Pick<ButtonCoreProps, 'disabled' | 'variant'>;
export const ButtonPropsContext = React.createContext<ButtonPropsContextProps>({
  disabled: false,
  variant: 'basic',
});

export const ButtonCore: OmakasePolymorphicComponent<
  ButtonCoreProps,
  typeof ButtonCoreDefaultElement
> = React.forwardRef(
  <E extends React.ElementType = typeof ButtonCoreDefaultElement>(
    {
      'aria-label': al,
      children,
      component,
      disabled = false,
      fullWidth,
      href,
      iconEnd,
      iconOnly,
      iconStart,
      onClick,
      selected,
      size,
      variant,
      ...rest
    }: OmakasePolymorphicComponentProps<ButtonCoreProps, E>,
    ref: React.Ref<HTMLButtonElement> | undefined
  ) => {
    const sharedProps = {
      'aria-label': al,
      css: (theme: Theme) => [
        buttonCoreBase(theme, variant),
        buttonCoreSize(theme, size),
        buttonCoreVariant(theme, variant, selected),
        iconEnd && iconEnd.slimPadding && buttonIconEnd(theme, size),
        iconOnly && buttonIconOnly(theme, size),
        fullWidth && buttonFullWidth,
      ],
      onClick: disabled ? undefined : onClick,
      ...rest,
    };

    const Component: React.ElementType = component || ButtonCoreDefaultElement;

    const buttonProps: { [key: string]: boolean | string | undefined } = {};
    if (Component === 'button') {
      buttonProps.disabled = disabled || undefined;
    } else {
      buttonProps['aria-disabled'] = disabled || undefined;
      buttonProps.href = disabled ? undefined : href;
    }

    const inner = (
      <>
        {iconStart && (
          <span css={(theme: Theme) => buttonIcon(theme, { size, position: 'start' })}>
            {iconStart}
          </span>
        )}
        {!iconOnly && (
          <span css={buttonContent} role="presentation">
            {children}
          </span>
        )}
        {iconOnly && (
          <span css={(theme: Theme) => buttonIcon(theme, { size, position: 'only' })}>
            {children}
          </span>
        )}
        {iconEnd && (
          <span
            css={(theme: Theme) =>
              buttonIcon(theme, {
                size,
                position: iconEnd.alignment === 'farRight' ? 'farRight' : 'end',
              })
            }
          >
            {iconEnd.icon}
          </span>
        )}
      </>
    );

    return (
      <OmakasePolymorphicRootComponent
        ref={ref}
        component={Component}
        {...buttonProps}
        {...sharedProps}
      >
        <ButtonPropsContext.Provider value={{ disabled, variant }}>
          {inner}
        </ButtonPropsContext.Provider>
      </OmakasePolymorphicRootComponent>
    );
  }
);
